import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Plus, Minus, MapPin } from 'lucide-react';

export default function StepCourts({
  selectedLocation, selectedDate, selectedTimeSlot, duration,
  courtsRequested, courtsPricing, availableSports, specialRequests,
  onAddCourt, onRemoveCourt, onUpdateSport, onSpecialRequestsChange,
  onBack, onNext,
}) {
  // Cap the number of courts a customer can select by the location's
  // courts_count. Defaults to 1 if the location config is missing.
  const maxCourts = Number(selectedLocation?.courts_count) || 1;
  const canAddMore = courtsRequested.length < maxCourts;
  return (
    <div className="space-y-6 animate-slide-up" data-testid="step-courts">
      <div>
        <h2 className="text-xl font-bold font-heading uppercase mb-4">Select Courts & Sports</h2>

        <Card className="p-3 mb-4 bba-card bba-card-accent">
          <p className="text-sm">
            <MapPin className="w-4 h-4 inline mr-1" />
            <strong>{selectedLocation?.name}</strong> - {selectedDate} at {selectedTimeSlot}
          </p>
        </Card>

        {courtsRequested.map((court, index) => (
          <Card key={court._uid || `court-${index}`} className="p-4 mb-3 bba-card bba-card-accent">
            <div className="flex items-center justify-between mb-3">
              <Label className="uppercase text-xs font-bold">Court {index + 1}</Label>
              {courtsRequested.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveCourt(index)}
                  data-testid={`remove-court-${index}`}
                >
                  <Minus className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="h-12 rounded-2xl border bg-blue-50 text-blue-800 font-semibold flex items-center px-4" data-testid={`sport-select-${index}`}>
              Basketball
            </div>
            {courtsPricing[index] && (
              <p className="text-sm text-muted-foreground mt-2">
                BND {courtsPricing[index].price_per_hour}/hr × {duration}hr = BND {courtsPricing[index].total}
              </p>
            )}
          </Card>
        ))}

        {canAddMore && (
          <Button
            variant="outline"
            onClick={onAddCourt}
            data-testid="add-court-button"
            className="w-full rounded-full border-2 border-dashed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Court
          </Button>
        )}
        {!canAddMore && maxCourts > 1 && (
          <p className="text-xs text-muted-foreground text-center" data-testid="courts-cap-note">
            Maximum {maxCourts} court{maxCourts === 1 ? '' : 's'} available at this venue
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="special-requests" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
          Special Requests (Optional)
        </Label>
        <Textarea
          id="special-requests"
          data-testid="special-requests-input"
          placeholder="Any special requirements? E.g. accessibility needs. Please contact BBA staff if you need recurring booking."
          value={specialRequests}
          onChange={(e) => onSpecialRequestsChange(e.target.value)}
          className="rounded-2xl"
          rows={3}
        />
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onBack}
          data-testid="courts-back-button"
          className="flex-1 h-14 rounded-full"
        >
          Back
        </Button>
        <Button
          onClick={onNext}
          data-testid="courts-next-button"
          className="flex-1 btn-primary h-14"
        >
          Review Booking
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
